import React, { useState, useCallback } from 'react'
import { IoArrowBack } from 'react-icons/io5'
import type { TeamLite, FixtureLite, LeagueOverviewOut } from '../types'
import { useLeagueOverview } from '../swr-config'
import { LeagueTable, TeamOfWeek, TopPlayersSection, GameweekFixtures } from './league'
import { HistoricalFixtureModal } from './match-analysis/components/HistoricalFixtureModal'
import { PlayerProfileModal } from './PlayerProfileModal'
import { getLeagueLogo } from '../utils/leagueBadge'

type LeagueTab = 'overview' | 'table' | 'fixtures' | 'stats'

interface LeaguePageProps {
  apiLeagueId: number
  leagueName?: string
  leagueLogo?: string | null
  onBack: () => void
  onFixtureClick?: (fixture: FixtureLite) => void
  onTeamClick?: (team: TeamLite) => void
}

const TABS: { id: LeagueTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'table', label: 'Table' },
  { id: 'fixtures', label: 'Fixtures' },
  { id: 'stats', label: 'Stats' },
]

export function LeaguePage({
  apiLeagueId,
  leagueName,
  leagueLogo,
  onBack,
  onFixtureClick,
  onTeamClick,
}: LeaguePageProps) {
  const [activeTab, setActiveTab] = useState<LeagueTab>('overview')

  // Fetch league overview data
  const { data, isLoading, error } = useLeagueOverview(apiLeagueId)

  // Modal states
  const [historicalFixtureId, setHistoricalFixtureId] = useState<number | null>(null)
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null)

  const handleFormFixtureClick = useCallback((fixtureId: number) => {
    setHistoricalFixtureId(fixtureId)
  }, [])

  const handlePlayerClick = useCallback((playerId: number) => {
    setSelectedPlayerId(playerId)
  }, [])

  // Determine display name and logo (with fallback to utility function)
  const displayName = data?.league_name || leagueName || 'League'
  const displayLogo = data?.league_logo || leagueLogo || getLeagueLogo(displayName)

  return (
    <div className="league-page">
      {/* Header */}
      <div className="league-page-header">
        <div className="league-page-header-top">
          <button className="league-page-back" onClick={onBack} aria-label="Go back">
            <IoArrowBack size={20} />
          </button>
          <div className="league-page-header-main">
            {displayLogo && (
              <img src={displayLogo} alt={displayName} className="league-page-logo" />
            )}
            <div className="league-page-name-section">
              <h1 className="league-page-name">{displayName}</h1>
              {data?.country && (
                <span className="league-page-country">{data.country}</span>
              )}
              {data?.season && (
                <span className="league-page-season">{data.season}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="league-page-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`league-page-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="league-page-loading">
          <div className="spinner" />
          <span>Loading league data...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="league-page-error">
          <span>Failed to load league data</span>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}

      {/* Tab Content */}
      {!isLoading && !error && data && (
        <div className="league-page-content">
          {activeTab === 'overview' && (
            <LeagueOverviewTab
              data={data}
              onTeamClick={onTeamClick}
              onFixtureClick={onFixtureClick}
              onFormFixtureClick={handleFormFixtureClick}
              onPlayerClick={handlePlayerClick}
            />
          )}

          {activeTab === 'table' && (
            <div className="league-table-tab">
              <div className="fm-card">
                <div className="fm-card-header">
                  <div className="league-table-header-left">
                    {displayLogo && (
                      <img src={displayLogo} alt="" className="league-table-league-logo" />
                    )}
                    <span>{displayName}</span>
                  </div>
                </div>
                <div className="fm-card-body">
                  <LeagueTable
                    standings={data.standings}
                    allTeamsForm={data.all_teams_form}
                    nextFixtures={data.next_fixtures}
                    onTeamClick={onTeamClick}
                    onFixtureClick={handleFormFixtureClick}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'fixtures' && (
            <div className="league-fixtures-tab">
              <div className="fm-card">
                <div className="fm-card-header">
                  <span>All Fixtures</span>
                </div>
                <div className="fm-card-body">
                  <LeagueAllFixtures
                    upcomingFixtures={data.next_fixtures}
                    onFixtureClick={onFixtureClick}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="league-stats-tab">
              <div className="fm-card">
                <div className="fm-card-header">
                  <span>League Statistics</span>
                </div>
                <div className="fm-card-body">
                  <LeagueStatsTab data={data} onPlayerClick={handlePlayerClick} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Historical Fixture Modal */}
      {historicalFixtureId !== null && (
        <HistoricalFixtureModal
          fixtureId={historicalFixtureId}
          onClose={() => setHistoricalFixtureId(null)}
        />
      )}

      {/* Player Profile Modal */}
      {selectedPlayerId !== null && (
        <PlayerProfileModal
          playerId={selectedPlayerId}
          onClose={() => setSelectedPlayerId(null)}
        />
      )}
    </div>
  )
}

// Overview Tab - Two column layout
function LeagueOverviewTab({
  data,
  onTeamClick,
  onFixtureClick,
  onFormFixtureClick,
  onPlayerClick,
}: {
  data: LeagueOverviewOut
  onTeamClick?: (team: TeamLite) => void
  onFixtureClick?: (fixture: FixtureLite) => void
  onFormFixtureClick?: (fixtureId: number) => void
  onPlayerClick?: (playerId: number) => void
}) {
  return (
    <div className="league-overview-grid">
      {/* Left column - League Table */}
      <div className="league-main-column">
        <div className="fm-card league-standings-card">
          <div className="fm-card-header">
            <div className="league-table-header-left">
              {data.league_logo && (
                <img src={data.league_logo} alt="" className="league-table-league-logo" />
              )}
              <span>Standings</span>
            </div>
          </div>
          <div className="fm-card-body league-table-body">
            <LeagueTable
              standings={data.standings}
              allTeamsForm={data.all_teams_form}
              nextFixtures={data.next_fixtures}
              onTeamClick={onTeamClick}
              onFixtureClick={onFormFixtureClick}
            />
          </div>
        </div>
      </div>

      {/* Right column - TOTW, Top Players, Fixtures */}
      <div className="league-side-column">
        {/* Team of the Week */}
        <div className="fm-card league-totw-card">
          <TeamOfWeek
            totw={data.team_of_week ?? null}
            onPlayerClick={onPlayerClick}
          />
        </div>

        {/* Top Players */}
        <div className="fm-card league-top-players-card">
          <div className="fm-card-header">
            <span>Top Players</span>
          </div>
          <div className="fm-card-body">
            <TopPlayersSection
              scorers={data.top_scorers}
              assisters={data.top_assisters}
              rated={data.top_rated}
              onPlayerClick={onPlayerClick}
            />
          </div>
        </div>

        {/* Next Gameweek Fixtures */}
        <div className="fm-card league-fixtures-card">
          <GameweekFixtures
            gameweek={data.next_gameweek}
            onFixtureClick={onFixtureClick}
          />
        </div>
      </div>
    </div>
  )
}

// All Fixtures display for Fixtures tab
function LeagueAllFixtures({
  upcomingFixtures,
  onFixtureClick,
}: {
  upcomingFixtures?: FixtureLite[]
  onFixtureClick?: (fixture: FixtureLite) => void
}) {
  if (!upcomingFixtures || upcomingFixtures.length === 0) {
    return <div className="league-fixtures-empty">No fixtures available</div>
  }

  // Group by date
  const fixturesByDate = upcomingFixtures.reduce((acc, fixture) => {
    const dateKey = new Date(fixture.kickoff_utc).toDateString()
    if (!acc[dateKey]) acc[dateKey] = []
    acc[dateKey].push(fixture)
    return acc
  }, {} as Record<string, FixtureLite[]>)

  return (
    <div className="league-all-fixtures">
      {Object.entries(fixturesByDate).map(([dateKey, fixtures]) => (
        <div key={dateKey} className="league-date-group">
          <div className="league-date-header">
            {new Date(fixtures[0].kickoff_utc).toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
          </div>
          {fixtures.map((fixture) => (
            <LeagueFixtureRow
              key={fixture.id}
              fixture={fixture}
              onClick={() => onFixtureClick?.(fixture)}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

// Fixture row for Fixtures tab
function LeagueFixtureRow({
  fixture,
  onClick,
}: {
  fixture: FixtureLite
  onClick?: () => void
}) {
  const isLive = ['1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(fixture.status || '')
  const isFinished = fixture.status === 'FT'

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <button
      className={`league-fixture-row ${isLive ? 'live' : ''}`}
      onClick={onClick}
      type="button"
    >
      <div className="league-fixture-teams">
        <div className="league-fixture-team home">
          {fixture.home_team.crest_url && (
            <img src={fixture.home_team.crest_url} alt="" className="league-fixture-crest" />
          )}
          <span>{fixture.home_team.name}</span>
        </div>
        <div className="league-fixture-team away">
          {fixture.away_team.crest_url && (
            <img src={fixture.away_team.crest_url} alt="" className="league-fixture-crest" />
          )}
          <span>{fixture.away_team.name}</span>
        </div>
      </div>

      <div className="league-fixture-result">
        {isFinished ? (
          <span className="league-fixture-score">
            {fixture.home_score} - {fixture.away_score}
          </span>
        ) : isLive ? (
          <div className="league-fixture-live">
            <span className="league-fixture-score">
              {fixture.home_score ?? 0} - {fixture.away_score ?? 0}
            </span>
            <span className="league-live-badge">{fixture.status}</span>
          </div>
        ) : (
          <span className="league-fixture-time">{formatTime(fixture.kickoff_utc)}</span>
        )}
      </div>
    </button>
  )
}

// Stats Tab
function LeagueStatsTab({
  data,
  onPlayerClick,
}: {
  data: LeagueOverviewOut
  onPlayerClick?: (playerId: number) => void
}) {
  return (
    <div className="league-stats-content">
      <div className="league-stats-section">
        <h3 className="league-stats-section-title">Top Goalscorers</h3>
        <div className="league-stats-players">
          {data.top_scorers.map((player, idx) => (
            <button
              key={player.player_id}
              className="league-stats-player-row"
              onClick={() => onPlayerClick?.(player.player_id)}
              type="button"
            >
              <span className="league-stats-rank">{idx + 1}</span>
              <div className="league-stats-player-info">
                {player.photo_url && (
                  <img src={player.photo_url} alt="" className="league-stats-player-photo" />
                )}
                <div className="league-stats-player-text">
                  <span className="league-stats-player-name">{player.name}</span>
                  <span className="league-stats-player-team">{player.team_name}</span>
                </div>
              </div>
              <span className="league-stats-value">{player.stat_value}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="league-stats-section">
        <h3 className="league-stats-section-title">Top Assists</h3>
        <div className="league-stats-players">
          {data.top_assisters.map((player, idx) => (
            <button
              key={player.player_id}
              className="league-stats-player-row"
              onClick={() => onPlayerClick?.(player.player_id)}
              type="button"
            >
              <span className="league-stats-rank">{idx + 1}</span>
              <div className="league-stats-player-info">
                {player.photo_url && (
                  <img src={player.photo_url} alt="" className="league-stats-player-photo" />
                )}
                <div className="league-stats-player-text">
                  <span className="league-stats-player-name">{player.name}</span>
                  <span className="league-stats-player-team">{player.team_name}</span>
                </div>
              </div>
              <span className="league-stats-value">{player.stat_value}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="league-stats-section">
        <h3 className="league-stats-section-title">Top Rated Players</h3>
        <div className="league-stats-players">
          {data.top_rated.map((player, idx) => (
            <button
              key={player.player_id}
              className="league-stats-player-row"
              onClick={() => onPlayerClick?.(player.player_id)}
              type="button"
            >
              <span className="league-stats-rank">{idx + 1}</span>
              <div className="league-stats-player-info">
                {player.photo_url && (
                  <img src={player.photo_url} alt="" className="league-stats-player-photo" />
                )}
                <div className="league-stats-player-text">
                  <span className="league-stats-player-name">{player.name}</span>
                  <span className="league-stats-player-team">{player.team_name}</span>
                </div>
              </div>
              <span className="league-stats-value">{player.stat_value.toFixed(2)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
